// Payments on Plan & billing (owner, November 3): the plan the tenant pays
// for and what it has paid, read from Stripe each time the tab opens
// (GET /platform/leagues/:id/payments, API_CONTRACT.md › Payments). Nothing
// here writes: changes to a subscription are made in Stripe.

import { api } from '@/api'
import type { PaymentsInvoice, PaymentsPlan, TenantDetail, TenantPayments } from '@/api/types'
import { Badge, Button, Card, CardHeader, DataTable, ErrorState, InlineAlert, LoadingState, StatTile, type BadgeTone, type Column } from '@/components/ui'
import { ExternalLinkIcon } from '@/components/ui/icons'
import { formatDate } from '@/lib/format'
import { useQuery } from '@/lib/query'

function formatMoney(cents: number, currency = 'usd'): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100)
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`
  }
}

const INTERVAL = { day: 'day', week: 'week', month: 'month', year: 'year' } as Record<string, string>

function planPrice(p: PaymentsPlan): string | null {
  if (p.amount_cents === null) return null
  const each = INTERVAL[p.interval ?? ''] ?? p.interval
  const per = !each ? '' : p.interval_count > 1 ? ` every ${p.interval_count} ${each}s` : ` / ${each}`
  const qty = p.quantity > 1 ? ` × ${p.quantity}` : ''
  return `${formatMoney(p.amount_cents, p.currency)}${per}${qty}`
}

function planHint(p: PaymentsPlan): string {
  if (p.status === 'canceled') return 'Canceled in Stripe'
  if (p.cancel_at_period_end) return 'Cancels at the end of this period'
  if (p.status === 'past_due' || p.status === 'unpaid') return 'Payment overdue in Stripe'
  if (p.status === 'trialing') return p.next_payment_at ? `Trial; first payment ${formatDate(p.next_payment_at)}` : 'Trial'
  return p.next_payment_at ? `Next payment ${formatDate(p.next_payment_at)}` : 'No upcoming payment'
}

const STATUS: Record<string, { label: string; tone: BadgeTone }> = {
  paid: { label: 'Paid', tone: 'good' },
  open: { label: 'Unpaid', tone: 'warn' },
  uncollectible: { label: 'Uncollectible', tone: 'bad' },
}

function invoiceStatus(i: PaymentsInvoice) {
  if (i.status === 'paid' && i.refunded_cents > 0) {
    return i.refunded_cents >= i.amount_paid_cents ? { label: 'Refunded', tone: 'neutral' as BadgeTone } : { label: 'Partly refunded', tone: 'neutral' as BadgeTone }
  }
  return STATUS[i.status] ?? { label: i.status, tone: 'outline' as BadgeTone }
}

const COLUMNS: Column<PaymentsInvoice>[] = [
  { id: 'date', header: 'Date', cell: (i) => <span className="whitespace-nowrap tabular">{formatDate(i.created_at)}</span> },
  { id: 'what', header: 'For', primary: true, cell: (i) => <span className="text-ink">{i.description}</span> },
  {
    id: 'amount',
    header: 'Amount',
    align: 'right',
    cell: (i) => (
      <span className="whitespace-nowrap tabular">
        {formatMoney(i.status === 'paid' ? i.amount_paid_cents : i.amount_due_cents, i.currency)}
        {i.refunded_cents > 0 ? <span className="block text-xs text-muted">−{formatMoney(i.refunded_cents, i.currency)} refunded</span> : null}
      </span>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    cell: (i) => {
      const s = invoiceStatus(i)
      return <Badge tone={s.tone}>{s.label}</Badge>
    },
  },
  {
    id: 'link',
    header: <span className="sr-only">Invoice</span>,
    mobileLabel: 'Invoice',
    cell: (i) =>
      i.url ? (
        <a href={i.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-semibold text-accent hover:underline" aria-label={`Invoice ${i.number ?? ''} from ${formatDate(i.created_at)} in Stripe`.replace('  ', ' ')}>
          Invoice <ExternalLinkIcon size={13} />
        </a>
      ) : null,
  },
]

export default function PaymentsCard({ tenant }: { tenant: TenantDetail }) {
  const q = useQuery(['tenants', tenant.id, 'payments'], (signal) => api.tenants.payments(tenant.id, signal))
  return (
    <Card>
      <CardHeader title="Payments" description="From Stripe, read when this tab opens." />
      <PaymentsBody q={q} tenant={tenant} />
    </Card>
  )
}

function PaymentsBody({ q, tenant }: { q: ReturnType<typeof useQuery<TenantPayments>>; tenant: TenantDetail }) {
  if (q.status === 'error') return <ErrorState error={q.error} title="Couldn't load payments" onRetry={q.refetch} className="py-6" />
  const p = q.data
  if (!p) return <LoadingState label="Reading Stripe…" className="py-6" />

  if (p.stripe === 'not_configured') {
    return <InlineAlert title="Stripe isn't set up on this server">Payments can't be read here. They're in Stripe's dashboard.</InlineAlert>
  }
  if (p.stripe === 'no_customer') {
    return (
      <p className="text-[13px] text-muted">
        {tenant.account.billing_source === 'manual'
          ? 'Billed by hand, outside Stripe: ScoreLM has no record of what was paid.'
          : "No Stripe customer: this tenant hasn't paid through Stripe."}
      </p>
    )
  }
  if (p.stripe === 'unavailable') {
    return (
      <InlineAlert
        tone="warn"
        title="Couldn't read payments from Stripe"
        action={
          <Button size="sm" onClick={q.refetch} loading={q.isFetching}>
            Try again
          </Button>
        }
      >
        Stripe didn't answer, or doesn't know this customer{p.customer_id ? <> (<span className="font-mono">{p.customer_id}</span>)</> : null}.
      </InlineAlert>
    )
  }

  const price = p.plan ? planPrice(p.plan) : null
  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <StatTile
          label="Plan"
          value={p.plan ? <span className="text-xl">{price ?? p.plan.name ?? 'Subscription'}</span> : <span className="text-xl text-muted">No subscription</span>}
          hint={p.plan ? [p.plan.name && price ? p.plan.name : null, planHint(p.plan)].filter(Boolean).join(' · ') : 'Only one-off invoices in Stripe'}
          tone={p.plan && (p.plan.status === 'past_due' || p.plan.status === 'unpaid') ? 'warn' : 'default'}
        />
        <StatTile
          label="Paid to date"
          value={formatMoney(p.paid_cents, p.currency)}
          hint={`${p.payments_count} payment${p.payments_count === 1 ? '' : 's'}${p.refunded_cents > 0 ? ` · ${formatMoney(p.refunded_cents, p.currency)} refunded` : ''}${p.has_more ? ' · latest 100 invoices' : ''}`}
          tone="accent"
        />
      </div>

      {p.invoices.length > 0 ? (
        <DataTable caption="Invoices" rows={p.invoices} columns={COLUMNS} rowKey={(i) => i.id} />
      ) : (
        <p className="text-[13px] text-muted">No invoices yet.</p>
      )}

      {p.dashboard_url ? (
        <a href={p.dashboard_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 justify-self-start text-[13px] font-semibold text-accent hover:underline">
          Open the customer in Stripe <ExternalLinkIcon size={13} />
        </a>
      ) : null}
    </div>
  )
}
